import {Link, Navigate} from "react-router-dom";
import {FC, useCallback, useEffect, useState} from "react";
import {MdClose} from "react-icons/md";
import {FiEye, FiEyeOff} from "react-icons/fi";
import * as Yup from "yup";
import {ErrorMessage, Field, Form, Formik} from "formik";
import {SignInType} from "../../common/types/auth/sign-in.type.ts";
import {useMutation} from "@apollo/client";
import {SIGN_IN_MUTATION} from "../../api/graphql/mutations/sign-in.mutation.ts";
import {toast} from "react-toastify";
import {SignInResponseType} from "../../common/types/auth/sign-in-response.type.ts";
import {AuthInfoType} from "../../common/types/auth/auth-info.type.ts";
import {AppDispatch} from "../../store";
import {useDispatch} from "react-redux";
import {login} from "../../store/auth.store.ts";
import NotificationToast from "../../common/components/toasts/notification.toast.tsx";
import {NotificationType} from "../../common/enums/notification.enum.ts";

const initialValues: SignInType = {
    email: "",
    password: "",
};

const validationSchema: Yup.ObjectSchema<SignInType> = Yup.object({
    email: Yup.string().email("Invalid email address").required("Required"),
    password: Yup.string().min(6).required("Required"),
});

const SignInPage: FC = () => {
    const dispatcher: AppDispatch = useDispatch();
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [signIn, { data, loading, error }] = useMutation<SignInResponseType>(SIGN_IN_MUTATION);

    useEffect((): void => {
        if (error) {
            toast(<NotificationToast  type={NotificationType.ERROR} title="AuthError" message={error.message} />, {
                toastId: 'sign-in-bad-request'
            });
        }
    }, [error]);

    useEffect((): void => {
        if (data) {
            const response: AuthInfoType = data.signIn
            dispatcher(login(response))
        }
    }, [data, dispatcher]);

    const handleSubmit = useCallback(async (values: SignInType): Promise<void> => {
        await signIn({
            variables: {
                signInDto: values
            },
        });
    }, [signIn]);

    if (data) {
        return <Navigate to="/" />;
    }

    return (
        <div className="max-w-md w-96 mx-auto px-4 py-8">
            <h1 className="text-3xl text-slate-800 font-bold mb-6">Sign In</h1>
            {/* Form */}
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, setFieldValue, errors, touched, isValid }) => (
                    <Form>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1" htmlFor="email">Email Address</label>
                                <div className="relative">
                                    <Field
                                        id="email"
                                        type="email"
                                        name="email"
                                        placeholder="Email"
                                        className={`form-input w-full ${errors.email && touched.email ? "border-rose-300 text-rose-500 focus:border-rose-300" : ""}`}
                                    />

                                    <button
                                        onClick={() => setFieldValue('email', '')}
                                        className="absolute inset-0 left-auto group"
                                        tabIndex={-1}
                                        type="button"
                                        aria-label="Clear email"
                                    >
                                        <MdClose className="w-4 h-4 shrink-0 fill-current text-slate-400 group-hover:text-slate-500 ml-3 mr-2" />
                                    </button>
                                </div>

                                <ErrorMessage
                                    name="email"
                                    component="div"
                                    className="text-rose-500 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1" htmlFor="password">Password</label>
                                <div className="relative">
                                    <Field
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        placeholder="Password"
                                        className={`form-input w-full ${errors.password && touched.password ? "border-rose-300 text-rose-500 focus:border-rose-300" : ""}`}
                                    />

                                    <button
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-0 left-auto right-6 group"
                                        tabIndex={-1}
                                        type="button"
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword
                                            ? <FiEye className="w-4 h-4 shrink-0 text-slate-400 group-hover:text-slate-500 ml-3 mr-2" />
                                            : <FiEyeOff className="w-4 h-4 shrink-0 text-slate-400 group-hover:text-slate-500 ml-3 mr-2" />
                                        }
                                    </button>

                                    <button
                                        onClick={() => setFieldValue('password', '')}
                                        className="absolute inset-0 left-auto group"
                                        type="button"
                                        tabIndex={-1}
                                        aria-label="Clear password"
                                    >
                                        <MdClose className="w-4 h-4 shrink-0 fill-current text-slate-400 group-hover:text-slate-500 ml-3 mr-2" />
                                    </button>

                                </div>

                                <ErrorMessage
                                    name="password"
                                    component="div"
                                    className="text-rose-500 text-xs"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between mt-6">
                            <div className="mr-1">
                                <Link
                                    className="text-sm underline hover:no-underline"
                                    to="/reset-password"
                                    aria-label="Forgot password"
                                >Forgot Password?</Link>
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting || loading || !isValid}
                                aria-label="Sign In"
                                className={`btn ${(isSubmitting || loading || !isValid) ? 'disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed shadow-none' : ''} bg-indigo-500 hover:bg-indigo-600 text-white ml-3`}
                            >
                                {loading
                                    ? <>
                                        <svg className="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                                            <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                                        </svg>
                                        <span className="ml-2">Loading</span>
                                    </>
                                    : 'Sign In'
                                }

                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
            {/* Footer */}
            <div className="pt-5 mt-6 border-t border-slate-200">
                <div className="text-sm">
                    Don’t you have an account? <Link className="font-medium text-indigo-500 hover:text-indigo-600" to="/auth/sign-up">Sign Up</Link>
                </div>
            </div>
        </div>
    );
};

export default SignInPage;
